import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Paperclip, Loader2, Send, CheckCheck, MailOpen, Clock, Filter, X, Plus
} from 'lucide-react';
import emailService from '../services/emailService';
import api from '../services/api';
import { getFranchiseeIdForDataLoading } from '../App/services/appHelpers';
import { VIEWS } from '../App/utils/appConstants';
import useTechnicianFilter from '../hooks/useTechnicianFilter';
import useMobileHeaderActions from '../hooks/useMobileHeaderActions';
import MobileFAB from './MobileFAB';
import TechnicianFilter from './TechnicianFilter';

/** Wikimedia PDF file icon (CC-BY-SA) — red document with white "PDF" text */
const PdfIcon = ({ size = 22, className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size * 1.23}
    viewBox="0 0 75.32 92.6" className={className}>
    <g transform="translate(53.548 -183.975) scale(1.4843)">
      <path fill="#ff2116" d="M-29.633 123.947c-3.552 0-6.443 2.894-6.443 6.446v49.498c0 3.552 2.891 6.445 6.443 6.445H8.217c3.552 0 6.443-2.893 6.443-6.445v-40.701s.101-1.192-.416-2.352c-.485-1.087-1.275-1.844-1.275-1.844l-.006-.008-9.39-9.21-.016-.016s-.802-.763-1.99-1.273c-1.4-.601-2.842-.537-2.842-.537l.021-.003z"/>
      <path fill="#f5f5f5" d="M-29.633 126.064h28.379s1.135.011 1.965.367c.799.343 1.365.862 1.369.865l.004.004 9.367 9.19s.564.595.838 1.208c.22.495.234 1.4.234 1.4v40.747c0 2.416-1.91 4.328-4.326 4.328H-29.633c-2.416 0-4.326-1.912-4.326-4.328v-49.498c0-2.416 1.91-4.328 4.326-4.328z"/>
      <path fill="#ff2116" d="M-23.408 161.093c-1.457-1.457.12-3.458 4.397-5.584l2.691-1.337 1.048-2.294c.577-1.262 1.437-3.32 1.913-4.575l.864-2.28-.595-1.688c-.732-2.076-.993-5.195-.529-6.316.63-1.52 2.69-1.363 3.506.265.638 1.272.573 3.575-.183 6.48l-.619 2.381.545.926c.3.51 1.177 1.719 1.948 2.687l1.449 1.803 1.803-.235c5.73-.748 7.691.523 7.691 2.344 0 2.3-4.498 2.49-8.276-.164-.85-.597-1.434-1.19-1.434-1.19s-2.366.482-3.531.796c-1.203.324-1.803.527-3.565 1.122 0 0-.618.897-1.02 1.55-1.5 2.428-3.249 4.44-4.498 5.172-1.4.82-2.866.876-3.605.137zm2.286-.817c.819-.506 2.476-2.466 3.624-4.285l.464-.737-2.115 1.063c-3.266 1.643-4.76 3.19-3.984 4.127.437.526.959.482 2.011-.168zm21.218-5.956c.801-.56.685-1.691-.22-2.147-.705-.355-1.273-.428-3.104-.4-1.125.076-2.934.303-3.24.372 0 0 .994.687 1.435.94.587.335 2.014.957 3.056 1.276 1.028.315 1.623.282 2.073-.04zm-8.531-3.546c-.485-.51-1.31-1.573-1.832-2.363-.684-.897-1.026-1.53-1.026-1.53s-.5 1.607-.91 2.574l-1.278 3.16-.371.717s1.971-.646 2.974-.908c1.062-.278 3.218-.701 3.218-.701zm-2.75-11.026c.124-1.037.177-2.073-.157-2.596-.925-1.01-2.04-.168-1.851 2.235.063.809.264 2.19.533 3.042l.488 1.549.344-1.167c.189-.641.479-2.02.644-3.063z"/>
      <path fill="#2c2c2c" d="M-20.93 167.839h2.365q1.133 0 1.84.217.707.21 1.19.945.482.727.482 1.756 0 .944-.391 1.623-.392.679-1.057.98-.658.3-2.03.3h-.818v3.73h-1.581zm1.581 1.224v3.331h.784q1.05 0 1.448-.392.406-.392.406-1.274 0-.657-.266-1.063-.266-.413-.588-.504-.315-.098-1-.098zm5.507-1.224h2.148q1.56 0 2.49.553.938.553 1.414 1.644.483 1.092.483 2.421 0 1.4-.434 2.498-.427 1.092-1.316 1.763-.881.672-2.519.672h-2.267zm1.581 1.266v7.018h.658q1.378 0 2.001-.951.623-.959.623-2.554 0-3.513-2.624-3.513zm6.472-1.266h5.304v1.266h-3.723v2.855h2.981v1.266h-2.981v4.164h-1.581z"/>
    </g>
  </svg>
);

/**
 * Remove tracking pixel and fix centering from email HTML
 */
const cleanEmailHtml = (html) => {
  if (!html) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Remove tracking pixel images only (src contains emails/track/)
    doc.querySelectorAll('img').forEach(img => {
      if (img.src && img.src.includes('emails/track/')) {
        img.remove();
      }
    });

    // Override centering and remove signature border-top line
    doc.querySelectorAll('div').forEach(div => {
      const style = div.getAttribute('style') || '';
      if (style.includes('margin') && style.includes('auto')) {
        div.setAttribute('style', style
          .replace(/margin\s*:\s*[^;]+auto[^;]*/gi, 'margin: 0')
          .replace(/max-width\s*:\s*[^;]+/gi, 'max-width: 100%'));
      }
      if (style.includes('border-top')) {
        div.setAttribute('style', style.replace(/border-top\s*:\s*[^;]+;?/gi, ''));
      }
    });

    return doc.body.innerHTML;
  } catch {
    return html;
  }
};

/**
 * Smart date formatting:
 * Today = "14:32", this week = "Mon 14:32", this year = "3 Feb", older = "3 Feb 2025"
 */
const formatSmartDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const emailDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.floor((today - emailDay) / 86400000);
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  if (diffDays === 0) return time;
  if (diffDays > 0 && diffDays < 7) {
    const dayName = d.toLocaleDateString('en-GB', { weekday: 'short' });
    return `${dayName} ${time}`;
  }
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatFullDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

/**
 * Build download URL for an attachment based on its type
 */
const getAttachmentUrl = (att, emailFranchiseeId) => {
  const fId = emailFranchiseeId || 'all';
  // Paths without /api prefix — crudOperations adds it via baseURL
  switch (att.attachment_type) {
    case 'invoice':
      return `/invoices/${fId}/${att.source_id}/pdf`;
    case 'guarantee':
      return `/guarantees/${fId}/${att.source_id}/pdf`;
    case 'document_library':
      if (att.filepath) {
        const uploadsIdx = att.filepath.indexOf('uploads/');
        if (uploadsIdx !== -1) {
          return `/${att.filepath.substring(uploadsIdx)}`;
        }
      }
      return null;
    default:
      return null;
  }
};

const EmailList = ({ userProfile, onNavigate, initialSearch, onAddJob, onAddContract }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(initialSearch || '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [emailDetails, setEmailDetails] = useState({});
  const [loadingDetail, setLoadingDetail] = useState(null);
  const limit = 25;
  const techFilter = useTechnicianFilter(userProfile);

  useMobileHeaderActions(
    techFilter.showFilter ? <TechnicianFilter {...techFilter} compact /> : null,
    [techFilter.showFilter, techFilter.selectedTechnician, techFilter.techGroups]
  );

  const defaultFranchiseeId = userProfile?.role === 'admin' ? 'all' : userProfile?.id;
  const franchiseeId = techFilter.showFilter ? techFilter.getFilteredFranchiseeId() : defaultFranchiseeId;
  const branchId = techFilter.getSelectedBranchId();

  const loadEmails = useCallback(async () => {
    if (!franchiseeId) return;
    setLoading(true);
    try {
      const params = { page, limit, search };
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (branchId) params.branch_id = branchId;
      const result = await emailService.getEmails(franchiseeId, params);
      const emailList = result.emails || [];
      setEmails(emailList);
      setPagination(result.pagination || { total: 0, totalPages: 1 });

      // Auto-expand most recent email (first in list) on page 1
      if (emailList.length > 0 && page === 1) {
        const firstId = emailList[0].id;
        setExpandedEmail(firstId);
        // Load detail if not cached
        if (!emailDetails[firstId]) {
          setLoadingDetail(firstId);
          try {
            const detail = await emailService.getEmailDetail(franchiseeId, firstId);
            setEmailDetails(prev => ({ ...prev, [firstId]: detail }));
          } catch (err) {
            console.error('Error loading email detail:', err);
          } finally {
            setLoadingDetail(null);
          }
        }
      }
    } catch (error) {
      console.error('Error loading emails:', error);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  }, [franchiseeId, page, search, dateFrom, dateTo, branchId]);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const toggleExpand = async (emailId) => {
    if (expandedEmail === emailId) {
      setExpandedEmail(null);
      return;
    }
    setExpandedEmail(emailId);

    if (!emailDetails[emailId]) {
      setLoadingDetail(emailId);
      try {
        const result = await emailService.getEmailDetail(franchiseeId, emailId);
        setEmailDetails(prev => ({ ...prev, [emailId]: result }));
      } catch (error) {
        console.error('Error loading email detail:', error);
      } finally {
        setLoadingDetail(null);
      }
    }
  };

  const getRecipientDisplay = (email) => {
    if (email.customer_name) return email.customer_name;
    const emailAddr = email.to_email || '';
    const localPart = emailAddr.split('@')[0] || '';
    return localPart.charAt(0).toUpperCase() + localPart.slice(1);
  };

  // ── Expanded Detail Panel ──────────────────────────────────────────
  const renderExpandedDetail = (email) => {
    const detail = emailDetails[email.id];
    const isLoading = loadingDetail === email.id;

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={18} className="animate-spin text-blue-500 mr-2" />
          <span className="text-sm text-gray-500">Loading email details...</span>
        </div>
      );
    }

    const emailData = detail?.email || email;
    const attachments = detail?.attachments || [];
    const job = detail?.job || null;
    const cleanedHtml = cleanEmailHtml(emailData.message_html);

    return (
      <div className="p-4 md:p-5 bg-gray-50 border-t border-gray-200">
        {/* Metadata + Customer/Job/Read Receipt */}
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div className="text-sm space-y-0.5 min-w-0">
            <div className="text-gray-900"><span className="text-gray-500">To:</span> {emailData.to_email}</div>
            {emailData.from_email && (
              <div className="text-gray-900"><span className="text-gray-500">From:</span> {emailData.from_name ? `${emailData.from_name} <${emailData.from_email}>` : emailData.from_email}</div>
            )}
            <div className="text-gray-900"><span className="text-gray-500">Sent:</span> {formatFullDate(emailData.date_sent)}</div>
          </div>
          <div className="flex-shrink-0 text-right space-y-1.5">
            {job ? (
              <div>
                <button
                  onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate(VIEWS.JOB_DETAILS, { id: job.id }); }}
                  className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {emailData.customer_name || job.title || 'View Job'}
                </button>
              </div>
            ) : emailData.customer_name && emailData.customer_id ? (
              <div>
                <button
                  onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate(VIEWS.CUSTOMER_DETAILS, { id: emailData.customer_id, name: emailData.customer_name }); }}
                  className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {emailData.customer_name}
                </button>
              </div>
            ) : null}
            <div>
              {emailData.read_date ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  <MailOpen size={13} />
                  Opened {formatSmartDate(emailData.read_date)}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                  <Clock size={13} />
                  Not opened
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="mb-4">
            <span className="text-xs text-gray-500 font-medium">Attachments:</span>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              {attachments.map((att, idx) => {
                const url = getAttachmentUrl(att, emailData.franchisee_id);
                const isPdf = att.filename && att.filename.toLowerCase().endsWith('.pdf');

                const handleClick = async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!url) return;
                  try {
                    const response = await api.get(url, { responseType: 'blob' });
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const blobUrl = URL.createObjectURL(blob);
                    window.open(blobUrl, '_blank');
                  } catch (err) {
                    console.error('Error opening attachment:', err);
                  }
                };

                return (
                  <button
                    key={att.id || idx}
                    onClick={handleClick}
                    disabled={!url}
                    className={`inline-flex items-center gap-1.5 text-sm text-gray-900 hover:text-blue-600 transition-colors ${url ? 'cursor-pointer underline decoration-gray-300 hover:decoration-blue-500' : ''}`}
                  >
                    {isPdf ? (
                      <PdfIcon size={18} className="flex-shrink-0" />
                    ) : (
                      <Paperclip size={14} className="text-gray-500 flex-shrink-0" />
                    )}
                    <span>{att.filename}</span>
                    {att.file_size > 0 && (
                      <span className="text-xs text-gray-400">({formatFileSize(att.file_size)})</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Message Body */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-900 max-h-[500px] overflow-y-auto">
          <div dangerouslySetInnerHTML={{ __html: cleanedHtml }} />
        </div>

        {/* Read IP */}
        {emailData.read_date && emailData.read_ip && (
          <p className="mt-2 text-[11px] text-gray-400">
            Read from IP: {emailData.read_ip}
          </p>
        )}
      </div>
    );
  };

  // ── Desktop Row ────────────────────────────────────────────────────
  const renderDesktopRow = (email) => {
    const isExpanded = expandedEmail === email.id;
    const isRead = !!email.read_date;
    const recipientName = getRecipientDisplay(email);

    return (
      <div key={email.id}>
        <div
          className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors border-b border-gray-100
            ${isRead ? 'bg-white hover:bg-gray-50' : 'bg-blue-50/30 hover:bg-blue-50/50'}
          `}
          onClick={() => toggleExpand(email.id)}
        >
          {/* Status Icon */}
          <div className="flex-shrink-0 w-5">
            {isRead ? (
              <CheckCheck size={16} className="text-green-500" />
            ) : (
              <Send size={15} className="text-gray-400" />
            )}
          </div>

          {/* Recipient + Subject */}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              {email.customer_name && (email.job_id || email.customer_id) ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (email.job_id) {
                      onNavigate && onNavigate(VIEWS.JOB_DETAILS, { id: email.job_id });
                    } else {
                      onNavigate && onNavigate(VIEWS.CUSTOMER_DETAILS, { id: email.customer_id, name: email.customer_name });
                    }
                  }}
                  className={`truncate text-sm hover:underline ${isRead ? 'text-blue-600' : 'text-blue-700 font-semibold'}`}
                >
                  {recipientName}{email.customer_ref ? ` (${email.customer_ref})` : ''}
                </button>
              ) : (
                <span className={`truncate text-sm ${isRead ? 'text-gray-900' : 'text-black font-semibold'}`}>
                  {recipientName}
                </span>
              )}
              <span className="text-gray-400 hidden lg:inline">—</span>
              <span className={`truncate text-sm hidden lg:inline ${isRead ? 'text-gray-600' : 'text-gray-800'}`}>
                <span className="text-gray-400">Subject:</span> {email.subject}
              </span>
            </div>
            {/* Subject on own line for medium screens */}
            <div className="lg:hidden">
              <span className={`truncate block text-sm ${isRead ? 'text-gray-600' : 'text-gray-800'}`}>
                <span className="text-gray-400">Subject:</span> {email.subject}
              </span>
            </div>
            <p className="text-xs text-gray-500 truncate">To: {email.to_email}</p>
          </div>

          {/* Attachment indicator */}
          <div className="flex-shrink-0 w-10 text-center">
            {email.attachment_count > 0 && (
              <span className="inline-flex items-center gap-0.5 text-xs text-gray-500">
                <Paperclip size={13} />
                <span>{email.attachment_count}</span>
              </span>
            )}
          </div>

          {/* Smart Date */}
          <div className="flex-shrink-0 w-24 text-right">
            <span className={`text-xs ${isRead ? 'text-gray-500' : 'text-gray-700 font-medium'}`}>
              {formatSmartDate(email.date_sent)}
            </span>
          </div>

          {/* Expand Chevron */}
          <div className="flex-shrink-0 w-5">
            {isExpanded ? (
              <ChevronUp size={16} className="text-gray-400" />
            ) : (
              <ChevronDown size={16} className="text-gray-400" />
            )}
          </div>
        </div>

        {/* Expanded Detail */}
        {isExpanded && renderExpandedDetail(email)}
      </div>
    );
  };

  // ── Mobile Card ────────────────────────────────────────────────────
  const renderMobileCard = (email) => {
    const isExpanded = expandedEmail === email.id;
    const isRead = !!email.read_date;

    return (
      <div key={email.id} className={`border-b border-gray-100 ${isRead ? '' : 'bg-blue-50/20'}`}>
        <div
          className="p-3.5 cursor-pointer active:bg-gray-50"
          onClick={() => toggleExpand(email.id)}
        >
          {/* Line 1: Subject + icons */}
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <p className={`text-sm truncate flex-1 ${isRead ? 'text-gray-900' : 'text-black font-semibold'}`}>
              <span className="text-gray-400">Subject:</span> {email.subject}
            </p>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {email.attachment_count > 0 && (
                <span className="flex items-center gap-0.5 text-xs text-gray-500">
                  <Paperclip size={12} />{email.attachment_count}
                </span>
              )}
              {isRead ? (
                <CheckCheck size={14} className="text-green-500" />
              ) : (
                <Send size={13} className="text-gray-400" />
              )}
            </div>
          </div>
          {/* Line 2: To + date */}
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-gray-600 truncate flex-1">
              To: {email.customer_name || email.to_email}
            </p>
            <span className="text-xs text-gray-500 flex-shrink-0">
              {formatSmartDate(email.date_sent)}
            </span>
          </div>
        </div>

        {/* Expanded Detail */}
        {isExpanded && renderExpandedDetail(email)}
      </div>
    );
  };

  // ── Main Render ────────────────────────────────────────────────────
  return (
    <div className="px-2 py-2 md:p-6">
      {/* Header */}
      <MobileFAB onAddJob={onAddJob} onAddContract={onAddContract} />
      <div className="hidden md:flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Sent Emails</h1>
          <p className="text-gray-600">View all emails sent from PestCRM</p>
        </div>
        <div className="flex items-center gap-3">
          {onAddJob && (
            <button onClick={onAddJob} className="btn btn-md btn-secondary">
              <Plus size={16} />
              <span>Add Job</span>
            </button>
          )}
          {onAddContract && (
            <button onClick={onAddContract} className="btn btn-md btn-secondary">
              <Plus size={16} />
              <span>Add Contract</span>
            </button>
          )}
          <TechnicianFilter {...techFilter} />
        </div>
      </div>

      {/* Search + Date Filter */}
      <div className="mb-4 space-y-2">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, company, postcode, subject..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          {/* Mobile filter toggle */}
          <button
            onClick={() => setShowDateFilters(!showDateFilters)}
            className={`md:hidden p-2.5 border rounded-lg transition-colors ${
              (dateFrom || dateTo) ? 'border-pest-blue text-pest-blue bg-blue-50' : 'border-gray-300 text-gray-500 hover:bg-gray-50'
            }`}
            aria-label="Toggle date filters"
          >
            <Filter size={18} />
          </button>
          {/* Desktop date filters - always visible */}
          <div className="hidden md:flex items-center gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
                className="text-xs text-gray-500 hover:text-gray-700 mt-4"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        {/* Mobile date filters - expandable */}
        {showDateFilters && (
          <div className="md:hidden flex items-center gap-2 pt-1">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-0.5">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-0.5">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
                className="mt-4 p-1.5 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-blue-500 mr-2" />
            <span className="text-gray-500">Loading emails...</span>
          </div>
        ) : emails.length === 0 ? (
          <div className="text-center py-16">
            <Mail size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">No emails found</p>
            <p className="text-gray-400 text-sm mt-1">
              {search ? 'Try a different search term' : 'Emails sent from Customer Details will appear here'}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Rows */}
            <div className="hidden md:block">
              {emails.map(email => renderDesktopRow(email))}
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden">
              {emails.map(email => renderMobileCard(email))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <span className="text-sm text-gray-600">
                  Page {page} of {pagination.totalPages} ({pagination.total} emails)
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                    disabled={page === pagination.totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmailList;
